import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Checkbox,
	FormControlLabel,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	Grid,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { getTiposTickets, getTrabajadoresUsuario, postAsistentes } from "../../services/apis";
import { getPrecioTicketEvento } from "../eventos/eventosUtils";

const formatoEuro = (valor) => Number(valor || 0).toLocaleString("es-ES", {
	style: "currency",
	currency: "EUR",
});

const invitadosValor = (invitados, clave) => {
	const claves = {
		invitados: ["invitados"],
		invitados_nino1: ["invitados_nino1", "invitados_niño1", "invitados_niÃ±o1", "invitados_niÃƒÂ±o1"],
		invitados_nino2: ["invitados_nino2", "invitados_niño2", "invitados_niÃ±o2", "invitados_niÃƒÂ±o2"],
	};

	return (claves[clave] || []).reduce((valor, key) => valor || Number(invitados?.[key] || 0), 0);
};

const normalizarAsistente = (asistente) => ({
	...asistente,
	id_usuario: Number(asistente.id_usuario),
});

const ticketsDesdeAsistente = (asistente, tiposTickets) => {
	const valores = {};

	if (Array.isArray(asistente?.tickets) && asistente.tickets.length > 0) {
		asistente.tickets.forEach((ticket) => {
			valores[Number(ticket.id_tipo_ticket)] = Number(ticket.cantidad || 0);
		});
		return valores;
	}

	const legacy = [
		invitadosValor(asistente?.invitados, "invitados"),
		invitadosValor(asistente?.invitados, "invitados_nino1"),
		invitadosValor(asistente?.invitados, "invitados_nino2"),
	];

	tiposTickets.slice(0, 3).forEach((tipo, index) => {
		valores[Number(tipo.id)] = legacy[index] || 0;
	});

	return valores;
};

const ticketsAArray = (ticketsPorTipo) => (
	Object.entries(ticketsPorTipo)
		.map(([id, cantidad]) => ({
			id_tipo_ticket: Number(id),
			cantidad: Number(cantidad || 0),
		}))
		.filter((ticket) => ticket.id_tipo_ticket > 0 && ticket.cantidad > 0)
);

export const FormAsistirActividad = ({
	open,
	setOpen,
	tarea,
	setSuccesCrearComida,
	permiteInvitados = true,
}) => {
	const asistentesIniciales = useMemo(() => (
		(tarea?.asistentes || []).map(normalizarAsistente)
	), [tarea]);

	const [asistentes, setAsistentes] = useState(asistentesIniciales);
	const [usuarioActual, setUsuarioActual] = useState(null);
	const [tiposTickets, setTiposTickets] = useState([]);
	const miembroPagaEventos = Number(tarea?.miembro_paga || 0);
	const [miembroAsiste, setMiembroAsiste] = useState(true);
	const [miembroAsisteOriginal, setMiembroAsisteOriginal] = useState(true);
	const [tickets, setTickets] = useState({});
	const [ticketsOriginales, setTicketsOriginales] = useState(null);
	const [errors, setErrors] = useState({});
	const [submitError, setSubmitError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		setAsistentes(asistentesIniciales);
	}, [asistentesIniciales]);

	useEffect(() => {
		const cargarDatos = async () => {
			try {
				const [usuarioResponse, ticketsResponse] = await Promise.all([
					getTrabajadoresUsuario(),
					getTiposTickets(1, Number(String(tarea?.fecha || "").slice(0, 4)) || new Date().getFullYear()),
				]);

				const user = usuarioResponse?.data?.[0];
				if (user) {
					setUsuarioActual({
						id_usuario: Number(user.id),
						nombre: user.nombre,
					});
				} else {
					setSubmitError(usuarioResponse?.message || "No s'ha pogut carregar el teu usuari.");
				}

				if (ticketsResponse?.success) {
					setTiposTickets(ticketsResponse.data || []);
				}
			} catch (error) {
				console.error("Error carregant dades d'assistencia:", error);
				setSubmitError("No s'han pogut carregar les dades d'assistencia.");
			}
		};

		if (open) {
			cargarDatos();
		}
	}, [open]);

	useEffect(() => {
		if (!usuarioActual) {
			return;
		}

		const existente = asistentesIniciales.find(
			(asistente) => asistente.id_usuario === usuarioActual.id_usuario
		);
		const valores = existente ? ticketsDesdeAsistente(existente, tiposTickets) : {};

		setMiembroAsiste(Number(existente?.miembro_asiste ?? 1) === 1);
		setMiembroAsisteOriginal(Number(existente?.miembro_asiste ?? 1) === 1);
		setTickets(valores);
		setTicketsOriginales(existente ? valores : null);
	}, [asistentesIniciales, usuarioActual, tiposTickets]);

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		setOpen(false);
		setErrors({});
		setSubmitError("");
		setTickets({});
	};

	const handleChangeTicket = (idTipoTicket, value) => {
		const numValue = Math.max(0, parseInt(value, 10) || 0);
		setTickets((prev) => ({
			...prev,
			[Number(idTipoTicket)]: numValue,
		}));
	};

	const getTicketsAsistente = (asistente) => (
		ticketsDesdeAsistente(asistente, tiposTickets)
	);

	const getCantidadTicket = (asistente, idTipoTicket) => (
		Number(getTicketsAsistente(asistente)[Number(idTipoTicket)] || 0)
	);

	const totalTicketsActuales = Object.values(tickets).reduce((sum, val) => sum + Number(val || 0), 0);
	const ticketsPagan = tiposTickets.filter((tipo) => Number(tipo.paga) === 1);
	const totalTicketsEuros = ticketsPagan.reduce((sum, tipo) => (
		sum + (Number(tickets[Number(tipo.id)] || 0) * getPrecioTicketEvento(tarea, tipo))
	), 0);
	const precioMiembro = Number(tarea?.precio_miembro || 0);
	const totalAPagar = totalTicketsEuros + ((miembroAsiste && Number(miembroPagaEventos) === 1) ? precioMiembro : 0);

	const handleGuardar = async () => {
		if (!usuarioActual) {
			setSubmitError("No s'ha pogut carregar el teu usuari.");
			return;
		}

		if (permiteInvitados && totalTicketsActuales > 10) {
			setErrors({ tickets: "Maxim 10 convidats per persona" });
			return;
		}

		setErrors({});
		setIsSubmitting(true);
		setSubmitError("");

		const nuevoAsistente = {
			id_usuario: usuarioActual.id_usuario,
			nombre: usuarioActual.nombre,
			miembro_asiste: miembroAsiste ? 1 : 0,
			tickets: permiteInvitados ? ticketsAArray(tickets) : [],
			invitados: {},
		};

		const nuevosAsistentes = asistentes.some((asistente) => asistente.id_usuario === usuarioActual.id_usuario)
			? asistentes.map((asistente) => (
				asistente.id_usuario === usuarioActual.id_usuario ? nuevoAsistente : asistente
			))
			: [...asistentes, nuevoAsistente];

		try {
			const response = await postAsistentes({
				id_evento: tarea.id,
				asistentes: nuevosAsistentes,
			});

			if (response?.success) {
				setAsistentes(nuevosAsistentes);
				setSuccesCrearComida(true);
				setOpen(false);
				return;
			}

			setSubmitError(response?.message || "Error en guardar l'assistencia.");
		} catch (error) {
			console.error("Error guardant assistencia:", error);
			setSubmitError("Error de connexio. Intenta-ho de nou.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancelarAsistencia = async () => {
		if (!usuarioActual) {
			return;
		}

		const nuevosAsistentes = asistentes.filter(
			(asistente) => asistente.id_usuario !== usuarioActual.id_usuario
		);

		setIsSubmitting(true);
		setSubmitError("");

		try {
			const response = await postAsistentes({
				id_evento: tarea.id,
				asistentes: nuevosAsistentes,
			});

			if (response?.success) {
				setAsistentes(nuevosAsistentes);
				setSuccesCrearComida(true);
				setOpen(false);
				return;
			}

			setSubmitError(response?.message || "Error en cancel.lar l'assistencia.");
		} catch (error) {
			console.error("Error cancel.lant assistencia:", error);
			setSubmitError("Error de connexio. Intenta-ho de nou.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const totalesPorTipo = tiposTickets.reduce((acc, tipo) => {
		acc[Number(tipo.id)] = asistentes.reduce((sum, asistente) => (
			sum + getCantidadTicket(asistente, tipo.id)
		), 0);
		return acc;
	}, {});

	const yaAsiste = asistentes.some((asistente) => asistente.id_usuario === usuarioActual?.id_usuario);
	const ticketsCambiaron = miembroAsiste !== miembroAsisteOriginal || (permiteInvitados && JSON.stringify(tickets) !== JSON.stringify(ticketsOriginales || {}));
	const totalGeneral = asistentes.filter((asistente) => Number(asistente.miembro_asiste ?? 1) === 1).length + Object.values(totalesPorTipo).reduce((sum, val) => sum + Number(val || 0), 0);
	const hayPrecios = ticketsPagan.some((tipo) => getPrecioTicketEvento(tarea, tipo) > 0) || precioMiembro > 0;

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			<DialogTitle className="activity-assist-title">
				<HowToRegIcon />
				<Box>
					<Typography variant="h5">Confirmar assistencia</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						{tarea.asunto}
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={3}>
					{submitError && <Alert severity="error">{submitError}</Alert>}

					<Box className="activity-assist-info">
						<Typography variant="h6">Esdeveniment</Typography>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography><strong>Data:</strong> {tarea.fecha || "Sense data"}</Typography>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography><strong>Hora:</strong> {tarea.hora_inicio || "--:--"}{tarea.hora_final ? ` - ${tarea.hora_final}` : ""}</Typography>
							</Grid>
							{tarea.descripcion && (
								<Grid size={{ xs: 12 }}>
									<Typography><strong>Descripcio:</strong> {tarea.descripcion}</Typography>
								</Grid>
							)}
						</Grid>
					</Box>

					<Box>
						<Typography variant="h6">La teua assistencia</Typography>
						<Typography color="text.secondary">
							{usuarioActual?.nombre || "Carregant usuari..."}
						</Typography>

						<FormControlLabel label="Membre Asisteix" control={<Checkbox checked={miembroAsiste} onChange={(event) => setMiembroAsiste(event.target.checked)} />} />
						{permiteInvitados ? (
							<Grid container spacing={2} className="activity-assist-grid">
								{tiposTickets.map((tipo) => (
									<Grid key={tipo.id} size={{ xs: 12, sm: 6, md: 4 }}>
										<TextField
											type="number"
											label={tipo.nombre}
											value={tickets[Number(tipo.id)] || 0}
											onChange={(event) => handleChangeTicket(tipo.id, event.target.value)}
											fullWidth
											inputProps={{ min: 0 }}
											helperText={Number(tipo.paga) === 1 ? "Paga" : "No paga"}
										/>
									</Grid>
								))}
							</Grid>
						) : (
							<Typography color="text.secondary" className="activity-assist-grid">
								Aquest esdeveniment nomes permet confirmar assistents, sense convidats.
							</Typography>
						)}
						{errors.tickets && (
							<FormHelperText error>{errors.tickets}</FormHelperText>
						)}
						{hayPrecios && (
							<Box className="activity-assist-price">
								{(miembroAsiste && Number(miembroPagaEventos) === 1) && (
									<Typography><strong>La teua assistencia:</strong> {formatoEuro(precioMiembro)}</Typography>
								)}
								{ticketsPagan.map((tipo) => {
									const cantidad = Number(tickets[Number(tipo.id)] || 0);
									const precio = getPrecioTicketEvento(tarea, tipo);

									return (
										<Typography key={tipo.id}>
											<strong>{tipo.nombre}:</strong> {cantidad} x {formatoEuro(precio)} = {formatoEuro(cantidad * precio)}
										</Typography>
									);
								})}
								<Typography><strong>Total a pagar:</strong> {formatoEuro(totalAPagar)}</Typography>
								<Typography color="text.secondary">
									{(miembroAsiste && Number(miembroPagaEventos) === 1)
										? "El total inclou la teua assistencia i els tiquets marcats com a pagadors."
										: "El total nomes inclou els tiquets marcats com a pagadors."}
								</Typography>
							</Box>
						)}
					</Box>

					<Box>
						<Typography variant="h6">Llista d'assistents</Typography>
						<Typography color="primary">
							Total confirmats: <strong>{totalGeneral}</strong>
						</Typography>
						<Box className="activity-assist-table-wrap">
							<Table size="small" className="activity-assist-table">
								<TableHead>
									<TableRow>
										<TableCell>Nom</TableCell>
										{permiteInvitados && tiposTickets.map((tipo) => (
											<TableCell key={tipo.id} align="center">
												{tipo.nombre} ({totalesPorTipo[Number(tipo.id)] || 0})
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{asistentes.map((asistente) => (
										<TableRow key={asistente.id_usuario}>
											<TableCell>{asistente.nombre}</TableCell>
											{permiteInvitados && tiposTickets.map((tipo) => (
												<TableCell key={tipo.id} align="center">
													{getCantidadTicket(asistente, tipo.id)}
												</TableCell>
											))}
										</TableRow>
									))}
									{asistentes.length === 0 && (
										<TableRow>
											<TableCell colSpan={permiteInvitados ? Math.max(1, tiposTickets.length + 1) : 1} align="center">
												Encara no hi ha assistents confirmats
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</Box>
						{permiteInvitados && tiposTickets.length > 0 && (
							<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
								{tiposTickets.map((tipo) => (
									<Chip
										key={tipo.id}
										size="small"
										label={`${tipo.nombre}: ${Number(tipo.paga) === 1 ? "paga" : "no paga"}`}
										color={Number(tipo.paga) === 1 ? "primary" : "default"}
										variant={Number(tipo.paga) === 1 ? "filled" : "outlined"}
									/>
								))}
							</Stack>
						)}
					</Box>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} disabled={isSubmitting}>
					Tancar
				</Button>
				{yaAsiste && !ticketsCambiaron ? (
					<Button
						onClick={handleCancelarAsistencia}
						color="error"
						variant="outlined"
						disabled={isSubmitting}
					>
						{isSubmitting ? "Cancel.lant..." : "Cancel.lar assistencia"}
					</Button>
				) : (
					<Button
						onClick={handleGuardar}
						variant="contained"
						disabled={isSubmitting || !usuarioActual}
					>
						{isSubmitting ? "Guardant..." : yaAsiste ? "Actualitzar assistencia" : "Confirmar assistencia"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
